<template>
  <div class="trend-card trend-card--summary">
    <div class="trend-card__header">
      <div class="trend-card__title">
        <i class="fa-duotone fa-layer-group"></i>
        Work Queue
      </div>
    </div>

    <div class="trend-card__body">
      <div
        class="trend-loading summary-loading"
        v-if="rowsLoading || resubmittalsLoading || underwritesResubsLoading"
      >
        Loading...
      </div>

      <div
        class="trend-empty summary-empty"
        v-else-if="!safeRows.length && !safeResubmittals.length && !safeUnderwritesResubs.length"
      >
        No data found
      </div>

      <div class="uw-summary-split" v-else>
        <div class="uw-summary-card uw-summary-card--new">
          <div class="uw-summary-card__top">
            <div class="uw-summary-card__title">
              <i class="fa-duotone fa-file-shield"></i>
              New U/W
            </div>
          </div>

          <div class="uw-summary-card__grid uw-summary-card__grid--stack">
            <div class="uw-mini-metric uw-mini-metric--click" @click.stop="openUwResubsBucket('retail', 'sub')">
              <div class="uw-mini-metric__label">Retail</div>
              <div class="uw-mini-metric__value">{{ retailSubCount }}</div>
            </div>

            <div class="uw-mini-metric uw-mini-metric--click" @click.stop="openUwResubsBucket('wholesale', 'sub')">
              <div class="uw-mini-metric__label">Wholesale</div>
              <div class="uw-mini-metric__value">{{ wholesaleSubCount }}</div>
            </div>
          </div>

          <div class="uw-summary-card__footer" @click="openUwResubsBucket('all', 'sub')">
            <span class="uw-summary-card__footer-left">
              <i class="fa-duotone fa-arrow-up-right-from-square"></i>
              View details
            </span>
            <span class="uw-summary-card__footer-right">
              Updated {{ lastUpdatedLabel }}
            </span>
          </div>
        </div>

        <div class="uw-summary-card uw-summary-card--queue">
          <div class="uw-summary-card__top">
            <div class="uw-summary-card__title">
              <i class="fa-duotone fa-arrows-rotate"></i>
              Resub Queue
            </div>
          </div>

          <div class="uw-summary-card__grid uw-summary-card__grid--stack">
            <div class="uw-mini-metric uw-mini-metric--click" @click.stop="openUwResubsBucket('retail', 'resub')">
              <div class="uw-mini-metric__label">Retail</div>
              <div class="uw-mini-metric__value">{{ retailResubCount2 }}</div>
            </div>

            <div class="uw-mini-metric uw-mini-metric--click" @click.stop="openUwResubsBucket('wholesale', 'resub')">
              <div class="uw-mini-metric__label">Wholesale</div>
              <div class="uw-mini-metric__value">{{ wholesaleResubCount2 }}</div>
            </div>
          </div>

          <div class="uw-summary-card__footer" @click="openUwResubsBucket('all', 'resub')">
            <span class="uw-summary-card__footer-left">
              <i class="fa-duotone fa-arrow-up-right-from-square"></i>
              View details
            </span>
            <span class="uw-summary-card__footer-right">
              Updated {{ lastUpdatedLabel }}
            </span>
          </div>
        </div>

        <div class="uw-summary-card uw-summary-card--resub">
          <div class="uw-summary-card__top">
            <div class="uw-summary-card__title">
              <i class="fa-duotone fa-clock-exclamation"></i>
              Resubs outside of SLA
            </div>
          </div>

          <div class="uw-summary-card__grid uw-summary-card__grid--two">
            <div class="uw-mini-metric uw-mini-metric--click" @click="openResubBucket('retail')">
              <div class="uw-mini-metric__label">Retail</div>
              <div class="uw-mini-metric__value">{{ retailResubCount }}</div>
            </div>
            <div class="uw-mini-metric uw-mini-metric--click" @click="openResubBucket('wholesale')">
              <div class="uw-mini-metric__label">Wholesale</div>
              <div class="uw-mini-metric__value">{{ wholesaleResubCount }}</div>
            </div>
          </div>

          <div class="uw-summary-card__footer">
            <span class="uw-summary-card__footer-left" @click.stop="openResubBucket('wholesale')">
              <i class="fa-duotone fa-arrow-up-right-from-square"></i>
              View details
            </span>
            <span class="uw-summary-card__footer-right">
              Updated {{ lastUpdatedLabel }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="resubmittalsError" class="uw-resub-error">
        {{ resubmittalsError }}
      </div>

      <div v-if="underwritesResubsError" class="uw-resub-error">
        {{ underwritesResubsError }}
      </div>
    </div>

    <div v-if="isResubModalOpen" class="uw-resub-modal-overlay" @click="closeResubModal">
      <div
        class="uw-resub-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="activeResubBucketLabel"
        @click.stop
      >
        <div class="uw-resub-modal__top">
          <div class="uw-resub-modal__title">{{ activeResubBucketLabel }}</div>
          <button class="uw-resub-modal__close" @click="closeResubModal">
            <i class="fa-duotone fa-xmark"></i>
          </button>
        </div>

        <div class="uw-resub-modal__sub">
          {{ activeResubRows.length }} resubs
        </div>

        <div class="uw-resub-modal__search">
          <i class="fa-duotone fa-magnifying-glass"></i>
          <input
            v-model="resubModalSearch"
            type="text"
            placeholder="Search by underwriter or loan #..."
            class="uw-resub-modal__search-input"
          />
          <button v-if="resubModalSearch" class="uw-resub-modal__search-clear" @click="resubModalSearch = ''">
            <i class="fa-duotone fa-xmark"></i>
          </button>
        </div>

        <div class="uw-resub-modal__list">
          <div
            v-for="r in activeResubRows"
            :key="String(r.id || '') + String(r.logMSDateTimeResubmittalRaw || '')"
            class="uw-resub-row"
            @click="$emit('openResubmittalsOverBizHours', { bucket: activeResubBucket, row: r })"
          >
            <div class="uw-resub-row__top">
              <div class="uw-resub-row__loan">
                {{ r.f1411 || "—" }}
                <span class="uw-resub-row__divider">•</span>
                {{ r.f364 || r.msstatus || "—" }}
              </div>
              <div class="uw-resub-row__pill">
                {{ daysPastSlaLabel(r) }}
              </div>
            </div>

            <div class="uw-resub-row__mid">
              <div class="uw-resub-row__uw">
                <i class="fa-duotone fa-user"></i>
                {{ r.underwriterDisplayName || r.underwriterEmail || "—" }}
              </div>
              <div class="uw-resub-row__date">
                <i class="fa-duotone fa-calendar"></i>
                {{ resubDateLabel(r) }}
              </div>
            </div>
          </div>

          <div v-if="!activeResubRows.length" class="uw-resub-empty">
            No resubs in this bucket
          </div>
        </div>

        <div class="uw-resub-modal__footer">
          <button class="uw-resub-modal__btn" @click="closeResubModal">Close</button>
        </div>
      </div>
    </div>

    <div v-if="isUwResubsModalOpen" class="uw-resub-modal-overlay" @click="closeUwResubsModal">
      <div
        class="uw-resub-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="uwResubsModalTitle"
        @click.stop
      >
        <div class="uw-resub-modal__top">
          <div class="uw-resub-modal__title">{{ uwResubsModalTitle }}</div>
          <button class="uw-resub-modal__close" @click="closeUwResubsModal">
            <i class="fa-duotone fa-xmark"></i>
          </button>
        </div>

        <div class="uw-resub-modal__sub">
          {{ activeUwResubsRows.length }} loans
        </div>

        <div class="uw-resub-modal__search">
          <i class="fa-duotone fa-magnifying-glass"></i>
          <input
            v-model="uwResubsModalSearch"
            type="text"
            placeholder="Search by underwriter or loan #..."
            class="uw-resub-modal__search-input"
          />
          <button v-if="uwResubsModalSearch" class="uw-resub-modal__search-clear" @click="uwResubsModalSearch = ''">
            <i class="fa-duotone fa-xmark"></i>
          </button>
        </div>

        <div class="uw-resub-modal__list">
          <div v-for="r in activeUwResubsRows" :key="String(r.id || '') + String(r.f364 || '')" class="uw-resub-row">
            <div class="uw-resub-row__top">
              <div class="uw-resub-row__loan">
                {{ r.underwriterDisplayName || r.underwriterEmail || r.f1411 || "—" }}
                <span class="uw-resub-row__divider">•</span>
                {{ r.f364 || "—" }}
                <span class="uw-resub-row__divider">•</span>
                {{ r.msstatus || "—" }}
              </div>

              <div class="uw-resub-row__mid">
                <div class="uw-resub-row__uw"></div>
                <div class="uw-resub-row__date">
                  <i class="fa-duotone fa-calendar"></i>
                  {{ subOrResubDateLabel(r) }}
                </div>
              </div>
            </div>

            <div class="uw-resub-row__actions">
              <button class="uw-resub-action-btn" @click.stop="openSwiftWindow(r)">
                Open in Swift
              </button>
              <button class="uw-resub-action-btn uw-resub-action-btn--primary" @click.stop="openEncompassAndSwift(r)">
                Open in Encompass
              </button>
            </div>
          </div>

          <div v-if="!activeUwResubsRows.length" class="uw-resub-empty">
            No loans in this bucket
          </div>
        </div>

        <div class="uw-resub-modal__footer">
          <button class="uw-resub-modal__btn" @click="closeUwResubsModal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InitialUwTopSummaryCard",
  props: {
    rows: { type: Array, default: () => [] },
    rowsLoading: { type: Boolean, default: false },

    resubmittalsRows: { type: Array, default: () => [] },
    resubmittalsLoading: { type: Boolean, default: false },
    resubmittalsError: { type: String, default: null },

    underwritesResubsRows: { type: Array, default: () => [] },
    underwritesResubsLoading: { type: Boolean, default: false },
    underwritesResubsError: { type: String, default: null }
  },
  data() {
    return {
      isResubModalOpen: false,
      activeResubBucket: null,
      resubModalSearch: "",

      isUwResubsModalOpen: false,
      activeUwResubsChannel: "all",
      activeUwResubsType: "all",
      uwResubsModalSearch: "",

      lastUpdatedAt: null,
      nowTick: Date.now(),
      _nowTimer: null
    }
  },
  computed: {
    safeRows() {
      return Array.isArray(this.rows) ? this.rows : []
    },
    safeResubmittals() {
      return Array.isArray(this.resubmittalsRows) ? this.resubmittalsRows : []
    },
    safeUnderwritesResubs() {
      return Array.isArray(this.underwritesResubsRows) ? this.underwritesResubsRows : []
    },

    lastUpdatedLabel() {
      if (!this.lastUpdatedAt) return "—"

      const updated = new Date(this.lastUpdatedAt).getTime()
      if (Number.isNaN(updated)) return "—"

      const diffSec = Math.max(0, Math.floor((this.nowTick - updated) / 1000))

      if (diffSec < 5) return "just now"
      if (diffSec < 60) return `${diffSec}s ago`

      const diffMin = Math.floor(diffSec / 60)
      if (diffMin < 60) return `${diffMin}m ago`

      const diffHr = Math.floor(diffMin / 60)
      return `${diffHr}h ago`
    },

    attentionConditionCount() {
      const rows = this.safeRows
      const attention = rows.filter(r =>
        r.needsAttention === true &&
        r.isActive === true &&
        r.conditionType === "Underwriting"
      )
      return new Set(attention.map(x => x.conditionId)).size
    },

    wholesaleResubCount() {
      return new Set(this.wholesaleResubRows.map(x => x.id).filter(Boolean)).size
    },
    retailResubCount() {
      return new Set(this.retailResubRows.map(x => x.id).filter(Boolean)).size
    },

    wholesaleResubRows() {
      return this.safeResubmittals.filter(r => this.bucketFromRow(r) === "wholesale")
    },
    retailResubRows() {
      return this.safeResubmittals.filter(r => this.bucketFromRow(r) === "retail")
    },

    activeResubRows() {
      let rows = []
      if (this.activeResubBucket === "wholesale") rows = this.wholesaleResubRowsSorted
      else if (this.activeResubBucket === "retail") rows = this.retailResubRowsSorted
      const q = this.resubModalSearch.trim().toLowerCase()
      if (!q) return rows
      return rows.filter(r =>
        (r.underwriterDisplayName || r.underwriterEmail || "").toLowerCase().includes(q) ||
        (r.f364 || "").toLowerCase().includes(q) ||
        (r.f1411 || "").toLowerCase().includes(q)
      )
    },

    wholesaleResubRowsSorted() {
      return this.sortResubs(this.wholesaleResubRows)
    },
    retailResubRowsSorted() {
      return this.sortResubs(this.retailResubRows)
    },

    activeResubBucketLabel() {
      return this.activeResubBucket === "wholesale" ? "Wholesale resubs" : "Retail resubs"
    },

    wholesaleSubCount() {
      return new Set(
        this.safeUnderwritesResubs
          .filter(r => this.bucketFromRow(r) === "wholesale" && this.typeFromRow(r) === "sub")
          .map(x => x.id)
          .filter(Boolean)
      ).size
    },
    wholesaleResubCount2() {
      return new Set(
        this.safeUnderwritesResubs
          .filter(r => this.bucketFromRow(r) === "wholesale" && this.typeFromRow(r) === "resub")
          .map(x => x.id)
          .filter(Boolean)
      ).size
    },
    retailSubCount() {
      return new Set(
        this.safeUnderwritesResubs
          .filter(r => this.bucketFromRow(r) === "retail" && this.typeFromRow(r) === "sub")
          .map(x => x.id)
          .filter(Boolean)
      ).size
    },
    retailResubCount2() {
      return new Set(
        this.safeUnderwritesResubs
          .filter(r => this.bucketFromRow(r) === "retail" && this.typeFromRow(r) === "resub")
          .map(x => x.id)
          .filter(Boolean)
      ).size
    },

    activeUwResubsRows() {
      const q = this.uwResubsModalSearch.trim().toLowerCase()
      return this.safeUnderwritesResubs
        .filter(r => {
          const chOk = this.activeUwResubsChannel === "all" || this.bucketFromRow(r) === this.activeUwResubsChannel
          const tOk = this.activeUwResubsType === "all" || this.typeFromRow(r) === this.activeUwResubsType
          if (!chOk || !tOk) return false
          if (!q) return true
          return (
            (r.underwriterDisplayName || r.underwriterEmail || "").toLowerCase().includes(q) ||
            (r.f364 || "").toLowerCase().includes(q) ||
            (r.f1411 || "").toLowerCase().includes(q)
          )
        })
        .slice()
    },

    uwResubsModalTitle() {
      const ch = this.activeUwResubsChannel === "all" ? "All" : (this.activeUwResubsChannel === "wholesale" ? "Wholesale" : "Retail")
      const t = this.activeUwResubsType === "all" ? "Subs + Resubs" : (this.activeUwResubsType === "sub" ? "Subs" : "Resubs")
      return `${ch} ${t}`
    }
  },
  methods: {
    _startUpdatedTimer() {
      if (this._nowTimer) return
      this._nowTimer = setInterval(() => {
        this.nowTick = Date.now()
      }, 1000)
    },
    _stopUpdatedTimer() {
      if (!this._nowTimer) return
      clearInterval(this._nowTimer)
      this._nowTimer = null
    },

    openResubBucket(bucket) {
      this.activeResubBucket = bucket
      this.isResubModalOpen = true
      document.body.classList.add("uw-modal-lock")
    },
    closeResubModal() {
      this.isResubModalOpen = false
      this.activeResubBucket = null
      this.resubModalSearch = ""
      document.body.classList.remove("uw-modal-lock")
    },

    openUwResubsBucket(channel, type) {
      this.activeUwResubsChannel = channel
      this.activeUwResubsType = type
      this.isUwResubsModalOpen = true
      document.body.classList.add("uw-modal-lock")
    },
    closeUwResubsModal() {
      this.isUwResubsModalOpen = false
      this.activeUwResubsChannel = "all"
      this.activeUwResubsType = "all"
      this.uwResubsModalSearch = ""
      document.body.classList.remove("uw-modal-lock")
    },

    bucketFromRow(r) {
      const v = String(r?.f2626 || "").toLowerCase()
      if (v.includes("wholesale")) return "wholesale"
      return "retail"
    },

    typeFromRow(r) {
      const s = String(r?.msstatus || "").toLowerCase().trim()
      return s.includes("resub") ? "resub" : "sub"
    },

    bucketPillLabel(r) {
      const ch = this.bucketFromRow(r)
      const t = this.typeFromRow(r)
      return `${ch} ${t}`
    },

    subOrResubDateLabel(r) {
      const t = this.typeFromRow(r)
      if (t === "resub") {
        const d =
          r?.logMSDateTimeResubmittalET ||
          r?.logMSDateTimeResubmittalPT ||
          r?.logMSDateTimeResubmittalRaw ||
          r?.resubmittalDate
        return this.formatMaybeDate(d)
      }
      return this.formatMaybeDate(r?.logMSDateSubmittal)
    },

    formatMaybeDate(d) {
      if (!d) return "—"
      const s = String(d)
      if (s.startsWith("1900-01-01")) return "—"
      const dt = new Date(d)
      if (isNaN(dt.getTime())) return s
      return dt.toLocaleString()
    },

    openSwiftWindow(r) {
      const loanId = r?.id
      const condId = null
      if (!loanId) return

      const isDev =
        window.location.hostname === "localhost" ||
        window.location.hostname === "dev.swiftapp.ai"

      const baseUrl = isDev
        ? "https://dev-swift.goldstarfinancial.com"
        : "https://swift.goldstarfinancial.com"

      const url = condId
        ? `${baseUrl}?id=${encodeURIComponent(loanId)}&condid=${encodeURIComponent(condId)}`
        : `${baseUrl}?id=${encodeURIComponent(loanId)}`

      window.open(url, "_blank", "noopener,noreferrer,popup,width=1200,height=900,left=80,top=80")
    },

    openEncompassAndSwift(r) {
      const loanId = r?.id
      if (!loanId) return

      const encompassBase = "https://www.encompassloconnect.com/pipeline/"
      const encompassUrl = `${encompassBase}${loanId}`
      window.open(encompassUrl, "_blank", "noopener,noreferrer,popup,width=1200,height=900,left=80,top=80")

      this.openSwiftWindow(r)
    },

    resubDateLabel(r) {
      const d =
        r?.logMSDateTimeResubmittalET ||
        r?.logMSDateTimeResubmittalPT ||
        r?.resubmittalDate
      if (!d) return "—"
      const dt = new Date(d)
      if (isNaN(dt.getTime())) return String(d)
      return dt.toLocaleString()
    },

    daysPastSlaLabel(r) {
      const bizHours = typeof r?.bizHoursElapsed === "number" ? r.bizHoursElapsed : null
      if (bizHours == null) return "—"
      const thresholdMinutes = typeof r?.thresholdMinutes === "number" ? r.thresholdMinutes : 0
      const thresholdHours = thresholdMinutes / 60
      const past = Math.max(0, bizHours - thresholdHours)
      const days = past / 8
      if (!isFinite(days)) return "—"
      return `${days.toFixed(1)}d past SLA`
    },

    sortResubs(rows) {
      const clone = Array.isArray(rows) ? rows.slice() : []
      clone.sort((a, b) => {
        const ah = typeof a?.bizHoursElapsed === "number" ? a.bizHoursElapsed : -1
        const bh = typeof b?.bizHoursElapsed === "number" ? b.bizHoursElapsed : -1
        return bh - ah
      })
      return clone
    },

    onKeydown(e) {
      if (e.key === "Escape" && this.isResubModalOpen) this.closeResubModal()
      if (e.key === "Escape" && this.isUwResubsModalOpen) this.closeUwResubsModal()
    }
  },
  mounted() {
    this._startUpdatedTimer()
    window.addEventListener("keydown", this.onKeydown)
  },
  beforeUnmount() {
    this._stopUpdatedTimer()
    window.removeEventListener("keydown", this.onKeydown)
    document.body.classList.remove("uw-modal-lock")
  },
  watch: {
    rowsLoading: {
      immediate: true,
      handler(v) {
        if (v === false) this.lastUpdatedAt = new Date().toISOString()
      }
    },
    resubmittalsLoading: {
      immediate: true,
      handler(v) {
        if (v === false) this.lastUpdatedAt = new Date().toISOString()
      }
    },
    underwritesResubsLoading: {
      immediate: true,
      handler(v) {
        if (v === false) this.lastUpdatedAt = new Date().toISOString()
      }
    }
  }
}
</script>

<style scoped>
.uw-resub-error {
  margin-top: 10px;
  font-size: 11px;
  font-weight: 900;
  color: #b91c1c;
}

.uw-resub-row__actions {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.uw-resub-action-btn {
  border: 1px solid rgba(0, 0, 0, 0.10);
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
  height: 34px;
  padding: 0 12px;
  font-weight: 950;
  cursor: pointer;
  font-size: 11px;
}

.uw-resub-action-btn--primary {
  border-color: rgba(37, 99, 235, 0.35);
  background: rgba(37, 99, 235, 0.10);
}

.trend-card {
  background: var(--background);
  border: 1px solid var(--border-ltr);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.trend-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-ltr);
  background: rgba(5, 29, 58, 0.02);
}

.trend-card__title {
  font-size: 13px;
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--text-primary-on-background);
}

.trend-card__body {
  padding: 10px 12px;
  min-height: 0;
}

.trend-loading,
.trend-empty {
  height: 210px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: var(--text-secondary-on-background);
  gap: 10px;
}

.summary-loading,
.summary-empty {
  height: auto;
  min-height: 60px;
  border-radius: 12px;
}

.uw-summary-split {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
}

.uw-summary-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
}

.uw-summary-card {
  border: 1px solid var(--border-ltr);
  background: rgba(40, 110, 255, 0.04);
  border-radius: 12px;
  padding: 10px 10px;
  cursor: default;
  transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 120px;
  position: relative;
}

.uw-summary-card--new,
.uw-summary-card--queue {
  cursor: pointer;
}

.uw-summary-card--new:hover,
.uw-summary-card--queue:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
}

.uw-summary-card--queue {
  background: rgba(40, 110, 255, 0.025);
}

.uw-summary-card--resub {
  background: rgba(200, 80, 255, 0.06);
}

.uw-summary-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.uw-summary-card__title {
  font-weight: 950;
  font-size: 12px;
  color: var(--text-primary-on-background);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.uw-summary-card__grid {
  display: grid;
  align-items: center;
}

.uw-summary-card__grid--two {
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.uw-summary-card__grid--stack {
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.uw-mini-metric {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 10px;
  border-radius: 12px;
}

.uw-mini-metric--click {
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.55);
  transition: transform 120ms ease, box-shadow 120ms ease;
}

.uw-mini-metric--click:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.06);
}

.uw-mini-metric__label {
  font-size: 11px;
  font-weight: 900;
  opacity: 0.75;
}

.uw-mini-metric__value {
  font-size: 22px;
  font-weight: 950;
  line-height: 1.1;
}

.uw-summary-card__footer {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 11px;
  font-weight: 900;
  color: var(--text-secondary-on-background);
}

.uw-summary-card__footer-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.uw-summary-card__footer-right {
  opacity: 0.85;
}

.uw-resub-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 25000;
  background: rgba(15, 23, 42, 0.38);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
}

.uw-resub-modal {
  width: min(900px, calc(100vw - 28px));
  max-height: min(78vh, 820px);
  background: var(--background);
  border: 1px solid rgba(0, 0, 0, 0.10);
  border-radius: 16px;
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.30);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.uw-resub-modal__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.uw-resub-modal__title {
  font-weight: 950;
  font-size: 13px;
  color: var(--text-primary-on-background);
}

.uw-resub-modal__close {
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.02);
  border-radius: 10px;
  height: 32px;
  width: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.uw-resub-modal__sub {
  padding: 0 12px 10px 12px;
  font-size: 11px;
  font-weight: 900;
  color: var(--text-secondary-on-background);
  opacity: 0.85;
}

.uw-resub-modal__list {
  padding: 0 12px 12px 12px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.uw-resub-modal__footer {
  padding: 10px 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: flex-end;
}

.uw-resub-modal__btn {
  border: 1px solid rgba(0, 0, 0, 0.10);
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
  height: 36px;
  padding: 0 14px;
  font-weight: 950;
  cursor: pointer;
}

.uw-resub-row {
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
  padding: 10px 12px;
}

.uw-resub-row__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.uw-resub-row__loan {
  font-weight: 950;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.uw-resub-row__divider {
  opacity: 0.6;
  margin: 0 6px;
}

.uw-resub-row__pill {
  font-size: 10.5px;
  font-weight: 950;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(200, 80, 255, 0.10);
  white-space: nowrap;
}

.uw-resub-row__mid {
  margin-top: 8px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  font-size: 11px;
  font-weight: 900;
  color: var(--text-secondary-on-background);
}

.uw-resub-row__uw,
.uw-resub-row__date {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.uw-resub-empty {
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: var(--text-secondary-on-background);
}

.uw-resub-modal__search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 12px 10px 12px;
  padding: 0 12px;
  height: 36px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.02);
}

.uw-resub-modal__search i {
  font-size: 13px;
  opacity: 0.45;
  flex-shrink: 0;
}

.uw-resub-modal__search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 800;
  color: var(--text-primary-on-background);
  outline: none;
}

.uw-resub-modal__search-input::placeholder {
  color: rgba(15, 23, 42, 0.35);
  font-weight: 700;
}

.uw-resub-modal__search-clear {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  opacity: 0.45;
  flex-shrink: 0;
}

.uw-resub-modal__search-clear:hover {
  opacity: 1;
}

.uw-modal-lock {
  overflow: hidden !important;
}

@media (max-width: 1200px) {
  .uw-summary-split {
    grid-template-columns: 1fr;
  }
}
</style>